"use client"

import type React from "react"

import { useState } from "react"
import { Download, Upload, HelpCircle } from "lucide-react"
import type { StoredMapData } from "../utils/storageUtils"

// Update the Footer component props and click handlers
interface FooterProps {
  onAboutClick: () => void
  onContactClick: () => void
  onPrivacyClick: () => void
  onTermsClick: () => void
  currentData: StoredMapData
  onImport: (data: StoredMapData) => void
}

export function Footer({
  onAboutClick,
  onContactClick,
  onPrivacyClick,
  onTermsClick,
  currentData,
  onImport,
}: FooterProps) {
  const currentYear = new Date().getFullYear()
  const [showTooltip, setShowTooltip] = useState(false)

  // Function to handle export
  const handleExport = () => {
    const jsonData = JSON.stringify(currentData, null, 2)
    const blob = new Blob([jsonData], { type: "application/json" })
    const url = URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = url
    link.download = `territory-map-${new Date().toISOString().split("T")[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Function to handle file selection for import
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonString = e.target?.result as string
        const importedData = JSON.parse(jsonString) as StoredMapData
        onImport(importedData)
      } catch (error) {
        console.error("Error importing data:", error)
        alert("Invalid file format. Please select a valid territory map JSON file.")
      }
    }

    reader.readAsText(file)
  }

  return (
    <footer className="bg-gray-100 mt-8">
      <div className="max-w-7xl mx-auto py-8 px-4 overflow-hidden sm:px-6 lg:px-8">
        {/* Data persistence section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col">
              <h3 className="text-base font-medium text-gray-900 mb-1">Save & Restore Your Work</h3>
              <div className="flex items-center text-sm text-gray-600">
                <p>Your data is stored locally in your browser</p>
                <div className="relative ml-1">
                  <button
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Learn more about data storage"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>

                  {showTooltip && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-white rounded-lg shadow-lg text-xs text-gray-700 z-50">
                      <p className="mb-2">
                        <strong>Why Export/Import Data?</strong>
                      </p>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>Back up your territory maps</li>
                        <li>Transfer maps between devices</li>
                        <li>Share maps with colleagues</li>
                        <li>Restore previous versions</li>
                      </ul>
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-white"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center gap-1 shadow-sm"
              >
                <Download className="h-4 w-4" />
                <span>Export Data</span>
              </button>

              <label className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center gap-1 cursor-pointer shadow-sm">
                <Upload className="h-4 w-4" />
                <span>Import Data</span>
                <input type="file" accept=".json" onChange={handleFileChange} className="hidden" />
              </label>
            </div>
          </div>
        </div>

        {/* Links section */}
        <nav className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-8" aria-label="Footer">
          <button onClick={onAboutClick} className="text-base text-gray-500 hover:text-gray-900">
            About Us
          </button>
          <button onClick={onContactClick} className="text-base text-gray-500 hover:text-gray-900">
            Contact Us
          </button>
          <button onClick={onPrivacyClick} className="text-base text-gray-500 hover:text-gray-900">
            Privacy Policy
          </button>
          <button onClick={onTermsClick} className="text-base text-gray-500 hover:text-gray-900">
            Terms of Service
          </button>
        </nav>

        {/* Copyright section */}
        <div className="flex flex-col items-center">
          <p className="text-center text-base text-gray-400">
            &copy; {currentYear} Coverage Maps. All rights reserved.
          </p>
          <div className="mt-3 flex items-center">
            <span className="text-sm text-gray-500">Also visit our sister site: </span>
            <a
              href="https://orgchartcreator.com"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
            >
              OrgChartCreator.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
